<script setup lang="ts">
import LazyImage from "@/components/LazyImage.vue";
import MaterialCounts from "@/components/MaterialCounts.vue";
import { useRecommenderTracking } from "@/composables/useRecommenderTracking";
import { AppBookmarkableEnum, Collection } from "@/services/fobizzToolsApi";
import type { TrackingEventInputTrackingEventMeta } from "@/services/orval/internalApiV1";

export type Props = {
  collection: Collection;
  enableTracking?: boolean;
  trackingSubKind?: string;
  trackingUrl?: string;
};

const props = withDefaults(defineProps<Props>(), {
  enableTracking: false,
  trackingSubKind: undefined,
  trackingUrl: undefined,
});

const { d } = useI18n();

const { trackClick } = useRecommenderTracking();
const handleClick = () => {
  if (props.enableTracking && props.trackingSubKind && props.trackingUrl) {
    trackClick(props.trackingSubKind, props.trackingUrl, {
      id: props.collection.id,
      type: "Collection",
    } as TrackingEventInputTrackingEventMeta);
  }
};
</script>

<template>
  <div class="collection-tile">
    <div class="collection-tile__image-wrapper">
      <LazyImage
        v-if="collection?.image?.previewUrl"
        :src="collection.image.previewUrl"
        :alt="collection.name"
        class="collection-tile__image"
      />
    </div>

    <div class="collection-tile__kind">
      <span class="collection-tile__icon">
        <i class="bi bi-collection"> </i>
      </span>
      <span>{{ collection.modelName }}</span>
    </div>
    <div class="collection-tile__content">
      <div class="collection-tile__title">
        <a
          :href="`${collection.publicUrl}`"
          class="stretched-link"
          data-testid="link-material"
          @click="handleClick"
        >
          {{ collection.name }}
        </a>
      </div>
      <div class="collection-tile__footer">
        <div class="collection-tile__author">
          {{ collection.user.name }}
        </div>
        <span
          v-if="collection.publishedAt"
          class="d-none d-sm-flex text-muted font-xs"
        >
          {{ d(new Date(collection.publishedAt), "numeric") }}
        </span>
      </div>
    </div>
    <div class="collection-tile__actions">
      <MaterialCounts
        :kind="AppBookmarkableEnum.TOOLS_COLLECTION"
        :material-id="collection.id"
        :author-id="collection.userId"
        :bookmarks-count="collection.bookmarksCount"
        :name="collection.name || ''"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.collection-tile {
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1 1;
  height: 21rem;
  overflow: hidden;
  border-radius: $border-radius;
  background: $color-surface-white;
  color: $color-text;
  text-decoration: none;
  transition: transform 0.1s ease-out;
  box-shadow: $brand-box-shadow;

  &:hover {
    transform: scale(1.03);
  }

  @include media-breakpoint-down(sm) {
    flex-direction: row;
    height: 8rem;
    border-radius: $border-radius-md;
  }

  &__image-wrapper {
    position: relative;
    width: 100%;
    height: 11.5rem;
    object-fit: cover;
    display: block;
    overflow: hidden;

    @include media-breakpoint-down(sm) {
      display: block;
      position: relative;
      left: 0;
      height: auto;
      max-width: 4.85rem;
      max-height: 8.3125rem;
    }
  }

  &__image {
    object-fit: cover;
    width: 100%;
    min-height: 100%;

    @include media-breakpoint-down(sm) {
      height: 100%;
    }
  }

  &__kind {
    display: flex;
    align-items: center;
    position: absolute;
    top: 1.5rem;
    left: 1.5rem;
    width: fit-content;
    height: 1.75rem;
    border: 2px solid $gray-300;
    border-radius: $border-radius-md;
    padding: $spacer-1 $spacer-2 $spacer-1 0.15rem;
    background-color: $color-surface-white;
    color: $gray-600;
    font-size: $font-size-xs;

    @include media-breakpoint-down(sm) {
      top: 0.5rem;
      left: 5.35rem;
      border: none;
      z-index: 1;
    }
  }

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    background: $color-surface-white;
    border-radius: 50%;
    width: 24px;
    height: 24px;
    color: $gray-600;
    font-size: $font-size-xs;
  }

  &__content {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 9.95rem;
    max-height: 100%;
  }

  &__title {
    max-height: 4.25rem;
    overflow: hidden;
    text-overflow: ellipsis;
    padding: $spacer-3 $spacer-4 $spacer-2 $spacer-4;
    background: $color-surface-white;

    @include media-breakpoint-down(md) {
      overflow: auto;
      height: fit-content;
    }

    @include media-breakpoint-down(sm) {
      margin-bottom: $spacer-1;
      margin-top: 1.25rem;
      padding: $spacer-3;
      padding-bottom: $spacer-1;
    }

    a {
      display: -webkit-box;
      line-clamp: 2;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      word-wrap: anywhere;
      font-size: $font-size-base;
      font-weight: $font-weight-semibold;
      text-decoration: none;
      color: $color-text;

      @include media-breakpoint-down(sm) {
        font-size: $font-size-sm;
      }
    }
  }

  &__footer {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    justify-content: space-between;
    background: $color-surface-white;
    padding: 0 $spacer-4 $spacer-3;

    @include media-breakpoint-down(sm) {
      padding: 0 $spacer-3 0.75rem;
      width: 100%;
      height: fit-content;
    }
  }

  &__author {
    display: flex;
    align-items: center;
    color: $gray-600;
    font-size: $font-size-sm;

    @include media-breakpoint-down(sm) {
      font-size: $font-size-xs;
      margin-top: auto;
    }
  }

  &__actions {
    position: absolute;
    right: $spacer-4;
    bottom: $spacer-3;

    @include media-breakpoint-down(sm) {
      right: $spacer-3;
      bottom: 0.75rem;
    }
  }
}
</style>
